import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { useGetCalendarProviderQuery } from '../../../calendar/data/calendarApi'
import { useProviderCalendarLive } from '../../../calendar/hooks/useProviderCalendarLive'
import { addAlert, removeAlert } from '../../../data/alertActions'
import { getAlertsState } from '../../../data/alertsSelectors'
import { Alert as AlertType } from '../../../data/alertTypes'
import { getAuthConfig, getAuthIsLoggedIn, getAuthUserId } from '../../../data/auth/authSelectors'
import { getProviderDetailsCalendarState } from '../../../providers/data/providerSelectors'
import {
  LC_CALENDAR,
  LC_CALENDAR_CURRENT,
  LC_CALENDAR_SELECT_CALENDARS,
  SETTINGS,
} from '../../constants/routingConstants'

const StyledLink = styled(Link)(({ theme }) => ({
  paddingLeft: theme.spacing['8px'],
}))

export const AlertsContainer: React.FC<ConnectedProps<typeof connector>> = ({ addAlert, removeAlert }) => {
  const alerts: AlertType[] = useSelector(getAlertsState)
  const isLoggedIn: boolean = useSelector(getAuthIsLoggedIn)
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const {
    data: calendarProvider,
    isSuccess: hasCalendarProviderSuccess,
    isError: hasCalendarProviderError,
  } = useGetCalendarProviderQuery({ providerId: userId }, { skip: !userId })

  const hasRetrievedCalendarProvider = hasCalendarProviderSuccess || hasCalendarProviderError
  const isProviderCalendarLive = useProviderCalendarLive(config, calendarProvider)
  const providerCalendarState = useSelector(getProviderDetailsCalendarState)
  const { isDACalendarTransitionEnabled, isInProductCalendarEnabled, psaBanner } = useFlags()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isCalendarPath = pathname.startsWith(LC_CALENDAR.route) || pathname.startsWith(SETTINGS.route)
  const { colors } = useTheme()
  // Scrolling to the top is the default behavior.
  // Hence, we scroll to the top if at least one of the alerts does not have explicitly
  // `scrollToTop` set to `false`.
  const scrollToTop = alerts.some((alert: any) => {
    return alert?.scrollToTop !== false
  })

  const bannerID = 'PPUIPSABanner'
  const calendarConnectionID = 'DAProviderIPCBanner'

  useEffect(() => {
    if (psaBanner && psaBanner.enabled && isLoggedIn) {
      addAlert({
        show: true,
        contents: psaBanner.message,
        style: psaBanner.level,
        expires: false,
        id: bannerID,
      })
    } else {
      removeAlert(bannerID)
    }
  }, [addAlert, removeAlert, psaBanner, isLoggedIn])

  useEffect(() => {
    // We want to show a banner prompting DA providers with the LD flag enabled to enable IPC on every page except
    // those related to the calendar. Also, will not be included if the PSA banner is present.
    // There are 2 different versions of this banner depending on if this is a first time connection.
    if (
      isLoggedIn &&
      !psaBanner?.enabled &&
      config?.inProductCalendar?.hybridBookingMode &&
      !isCalendarPath &&
      isInProductCalendarEnabled &&
      hasRetrievedCalendarProvider &&
      !isProviderCalendarLive &&
      providerCalendarState &&
      (providerCalendarState != 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE' || isDACalendarTransitionEnabled)
    ) {
      // This will prevent the calendaring banner from clobbering any other banner
      if (!alerts || !alerts.length) {
        addAlert({
          show: true,
          contents:
            providerCalendarState != 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE' ? (
              <FormattedMessage
                defaultMessage='The new and improved Lyra Calendar is now available to streamline your referrals. {link}'
                description='Banner text encouraging providers to connect to the new calendar'
                values={{
                  link: (
                    <StyledLink
                      testID={tID('AlertsContainer-DA-IPC-link')}
                      text='Connect now!'
                      onPress={() => {
                        track({ event: mixpanelEvents.OPEN_IPC_THROUGH_NEW_CONNECTION_BANNER })
                        navigate(LC_CALENDAR_CURRENT.route)
                      }}
                      underline
                      color={colors.textPrimary}
                    />
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage='Update to the new Lyra calendar {link}'
                description='Banner text guiding providers to update to the new calendar'
                values={{
                  link: (
                    <StyledLink
                      testID={tID('AlertsContainer-DA-calendar-transition-link')}
                      text='Update now'
                      onPress={() => {
                        track({ event: mixpanelEvents.OPEN_IPC_THROUGH_TRANSITION_BANNER })
                        navigate(LC_CALENDAR_SELECT_CALENDARS.route)
                      }}
                      underline
                      color={colors.textPrimary}
                    />
                  ),
                }}
              />
            ),
          style: 'info',
          expires: false,
          id: calendarConnectionID,
        })
      }
    } else {
      removeAlert(calendarConnectionID)
    }
    // We cannot add alerts to this dependency array, or it will cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addAlert,
    colors,
    config,
    hasRetrievedCalendarProvider,
    isCalendarPath,
    isDACalendarTransitionEnabled,
    isInProductCalendarEnabled,
    isLoggedIn,
    isProviderCalendarLive,
    navigate,
    pathname,
    providerCalendarState,
    psaBanner,
    removeAlert,
  ])

  useEffect(() => {
    if (scrollToTop) {
      window.scrollTo(0, 0)
    }
  }, [scrollToTop, alerts.length])

  if ((!isLoggedIn && !psaBanner?.enabled) || !alerts || !alerts.length) {
    return null
  }

  return (
    <div>
      {alerts.map((alert: any, idx: any) => (
        <AlertBanner
          dismissHandler={() => removeAlert(alert.id)}
          includeX={alert.id !== bannerID}
          key={`alert_${idx}`}
          show={alert.show}
          autoDismissTimer={alert.autoDismissTimer}
          style={alert.style}
          contents={alert.contents}
        />
      ))}
    </div>
  )
}

const connector = connect(null, { addAlert, removeAlert })

export default connector(AlertsContainer)
